import { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useQueryClient } from "@tanstack/react-query";
import { z } from "zod";
import { Alert, Button, Panel, TextArea, TextField } from "../../components/ui.tsx";
import { ConfirmDialog } from "../../components/dialog.tsx";
import { useToast } from "../../components/toast.tsx";
import { ApiError, applyFieldErrors, patch, post } from "../../lib/api.ts";
import { planKey, usePlan } from "../../lib/plan.tsx";
import { useUnsavedGuard } from "../../lib/guard.tsx";
import type { PlanDetail } from "../../lib/types.ts";
import { formatInstantBR, localDateOf } from "../../../shared/dates.ts";
import { InstitutionLogoPanel } from "../../components/institution-logo.tsx";
import { isoDate, reasonSchema, requiredText } from "../../../shared/setup.ts";
import { LIMITS } from "../../../shared/limits.ts";

const identity = z
  .object({
    name: requiredText(LIMITS.name, "Informe o nome da oficina."),
    facilitator_name: requiredText(LIMITS.name, "Informe o facilitador."),
    starts_on: isoDate("Informe a data de início."),
    ends_on: isoDate("Informe a data de término."),
    institution_name: requiredText(LIMITS.name, "Informe o nome da instituição."),
  })
  .refine((v) => v.ends_on >= v.starts_on, { path: ["ends_on"], message: "O término não pode ser anterior ao início." });

const deadline = z.object({ access_until: isoDate("Informe a nova data."), reason: reasonSchema });

export function WorkshopTab() {
  const plan = usePlan();
  const qc = useQueryClient();
  const toast = useToast();
  const [conflict, setConflict] = useState<string | null>(null);
  const [confirm, setConfirm] = useState<{ message: string; values: z.input<typeof deadline> } | null>(null);
  const [savingDeadline, setSavingDeadline] = useState(false);
  const [now] = useState(() => Date.now());

  const form = useForm<z.input<typeof identity>>({
    resolver: zodResolver(identity),
    values: { name: plan.name, facilitator_name: plan.facilitator_name, starts_on: plan.starts_on, ends_on: plan.ends_on, institution_name: plan.institution.name },
  });
  const dl = useForm<z.input<typeof deadline>>({
    resolver: zodResolver(deadline),
    defaultValues: { access_until: localDateOf(plan.access_until), reason: "" },
  });
  useUnsavedGuard(form.formState.isDirty || dl.formState.isDirty);

  const refresh = (p?: PlanDetail) => {
    if (p) qc.setQueryData(planKey(plan.id, "detail"), p);
    void qc.invalidateQueries({ queryKey: ["plans"] });
  };

  const saveIdentity = form.handleSubmit(async (v) => {
    setConflict(null);
    try {
      const res = await patch<{ data: PlanDetail }>(`/plans/${plan.id}`, { ...v, expected_version: plan.version });
      refresh(res.data);
      form.reset(v);
      toast("success", "Dados da oficina salvos.");
    } catch (e) {
      if (e instanceof ApiError && e.code === "VERSION_CONFLICT") setConflict(e.message);
      else {
        applyFieldErrors(e, form.setError as never);
        toast("danger", e instanceof Error ? e.message : "Não foi possível salvar.");
      }
    }
  });

  async function sendDeadline(values: z.input<typeof deadline>, confirmed: boolean) {
    setSavingDeadline(true);
    try {
      const res = await post<{ data: PlanDetail }>(`/plans/${plan.id}/access-deadline`, {
        ...values,
        expected_version: plan.version,
        confirm_immediate_cutoff: confirmed,
      });
      refresh(res.data);
      dl.reset({ access_until: localDateOf(res.data.access_until), reason: "" });
      setConfirm(null);
      toast("success", "Prazo de acesso atualizado.");
    } catch (e) {
      if (e instanceof ApiError && e.code === "CONFIRMATION_REQUIRED") setConfirm({ message: e.message, values });
      else if (e instanceof ApiError && e.code === "VERSION_CONFLICT") {
        setConfirm(null);
        setConflict(e.message);
      } else {
        setConfirm(null);
        applyFieldErrors(e, dl.setError as never);
        toast("danger", e instanceof Error ? e.message : "Não foi possível salvar.");
      }
    } finally {
      setSavingDeadline(false);
    }
  }

  const expired = now > new Date(plan.access_until).getTime();

  return (
    <div className="grid gap-5 lg:grid-cols-2">
      {conflict && (
        <div className="lg:col-span-2">
          <Alert tone="warn" title="Versão desatualizada" action={
            <Button variant="secondary" onClick={() => { setConflict(null); void qc.invalidateQueries({ queryKey: planKey(plan.id, "detail") }); }}>Carregar versão atual</Button>
          }>{conflict}</Alert>
        </div>
      )}
      <Panel>
        <h2 className="text-lg font-bold">Identificação da oficina</h2>
        <form noValidate onSubmit={saveIdentity}>
          <TextField label="Nome da oficina" required error={form.formState.errors.name?.message} {...form.register("name")} />
          <TextField label="Facilitador" required error={form.formState.errors.facilitator_name?.message} {...form.register("facilitator_name")} />
          <div className="grid gap-x-4 sm:grid-cols-2">
            <TextField label="Início" type="date" required error={form.formState.errors.starts_on?.message} {...form.register("starts_on")} />
            <TextField label="Término" type="date" required error={form.formState.errors.ends_on?.message} {...form.register("ends_on")} />
          </div>
          <TextField label="Dimensão" value="Gestão de Pessoas" readOnly hint="A dimensão é fixa nesta versão." />
          <TextField label="Instituição" required hint="O nome vale para todas as oficinas desta instituição." error={form.formState.errors.institution_name?.message} {...form.register("institution_name")} />
          <Button type="submit" loading={form.formState.isSubmitting} disabled={!form.formState.isDirty}>Salvar identificação</Button>
        </form>
      </Panel>
      <Panel>
        <h2 className="text-lg font-bold">Prazo de acesso dos participantes</h2>
        <p className="mt-1 text-sm">
          Atual: <strong>{formatInstantBR(plan.access_until)}</strong> (horário de Brasília)
        </p>
        {expired && <Alert tone="warn">O acesso dos participantes está encerrado. Os dados continuam preservados.</Alert>}
        <p className="mt-2 text-xs text-muted">
          O acesso termina às 23:59 da data escolhida. Depois disso, participantes não consultam, editam nem exportam a oficina. O instrutor mantém acesso.
        </p>
        <form noValidate onSubmit={dl.handleSubmit((v) => sendDeadline(v, false))}>
          <TextField label="Acesso até" type="date" required error={dl.formState.errors.access_until?.message} {...dl.register("access_until")} />
          <TextArea label="Justificativa" required rows={2} error={dl.formState.errors.reason?.message} {...dl.register("reason")} />
          <Button type="submit" loading={savingDeadline && !confirm}>Atualizar prazo</Button>
        </form>
      </Panel>
      <InstitutionLogoPanel />
      <ConfirmDialog
        open={confirm !== null}
        onOpenChange={(o) => !o && setConfirm(null)}
        title="Confirmar encerramento do acesso"
        confirmLabel="Confirmar novo prazo"
        danger
        loading={savingDeadline}
        onConfirm={() => confirm && void sendDeadline(confirm.values, true)}
      >
        <p className="text-sm">{confirm?.message}</p>
        <p className="mt-2 text-sm text-muted">Nenhum dado será apagado. Você poderá prorrogar o prazo depois.</p>
      </ConfirmDialog>
    </div>
  );
}
